import { useState, useCallback } from 'react';
import Box from '@material-ui/core/Box';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Logout from '@material-ui/icons/ExitToApp';
import { Divider, IconButton } from '@material-ui/core';
import { useStyles } from './NavbarMenu.styles';
import { useHistory } from 'react-router-dom';
import { Prompt } from '../../Prompt/Prompt.component';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpdIcon from '@material-ui/icons/ArrowDropUp';
import { UserProfile } from '../../UserProfile/UserProfile.component';
import { getUserProfile } from '../../../services/users/users.service';
import { IUserProfile } from '../../UserProfile/IUserProfile.types';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { ReactComponent as SwitchToExaminer } from '../../../assets/icons/switch-to-examiner.svg';
import { ReactComponent as SwitchToAdmin } from '../../../assets/icons/switch-to-admin.svg';
import { viewType } from '../Layout.types';
import usePermission, { UserPermission } from '../../../hooks/usePermission';
import { Paths } from '../../../utils/routerUrls';
import { leaveAssessmentSession } from '../../../Context/SignalR.context';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { setAssessmentSessionEvent } from '../../../services/scoring/scoring.service';
import { useLayoutContext } from '../../../Context/Layout.context';
import { needToNotifyAboutLeaveAssessmentProcess } from '../../../utils/assessmentSessionProcess';
import useSessionUser from '../../../hooks/useSessionUser';
import { useTranslation } from 'react-i18next';

const NavbarMenu: React.FC<{ viewType?: viewType; userNewName: (value?: string) => void }> = ({
  viewType,
  userNewName,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [openUserProfile, setOpenUserProfile] = useState<boolean>(false);
  const [profile, setProfile] = useState<IUserProfile | undefined>();
  const [userName, setUserName] = useState<string>();

  const assessmentSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);
  const { userAssessmentSessionInfo } = useSessionUser();
  const userFunction = userAssessmentSessionInfo.function;

  const examinerIsWorking = sessionStorage.getItem('startWork');

  const { isAnyDropdownOpen } = useLayoutContext();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    isAnyDropdownOpen.current = true;
  };

  const handleClose = () => {
    setAnchorEl(null);
    isAnyDropdownOpen.current = false;
  };

  const handleLogoffMenuClick = () => {
    setOpenConfirmation((state) => !state);
  };

  const isAdminView = viewType && viewType === 'admin';

  const handleLogoff = () => {
    if (examinerIsWorking === 'true' && !isAdminView) {
      leaveAssessmentSession(assessmentSession);
      if (needToNotifyAboutLeaveAssessmentProcess(assessmentSession, userFunction)) {
        setAssessmentSessionEvent(2)
          .then(
            () => {
              sessionStorage.removeItem('startWork');
            },
            (error) => {
              console.log(error);
            },
          )
          .finally(() => {
            history.push(Paths.logout);
          });
      } else history.push(Paths.logout);
    } else {
      history.push(Paths.logout);
    }
  };

  const handleGetUserProfile = useCallback(() => {
    getUserProfile()
      .then((result) => {
        if (result.data) {
          setProfile(result.data);
          setUserName(`${result.data.name} ${result.data.surname}`);
          setOpenUserProfile(true);
        }
      })
      .catch(() => {
        setProfile(undefined);
      });
  }, []);

  const handleUserProfileClose = () => {
    userNewName(userName ? userName : undefined);
    setOpenUserProfile(false);
  };
  const adminExaminerPermission = usePermission([
    UserPermission.AdminPanelManage,
    UserPermission.ExaminerDashboardRead,
  ]);

  const handleSwitchMode = () => {
    if (!isAdminView) {
      sessionStorage.setItem('context', 'admin');
      history.push(Paths.adminDashboard);
    } else {
      sessionStorage.setItem('context', 'examiner');
      history.push(Paths.dashboard);
    }
  };

  const arrow = open ? <ArrowDropUpdIcon /> : <ArrowDropDownIcon />;
  const modeType = isAdminView ? 'egzaminatora' : 'obsługi systemu';
  const iconModeType = isAdminView ? <SwitchToExaminer /> : <SwitchToAdmin />;

  return (
    <>
      <Box className={classes.box}>
        <TooltipOne title="Menu">
          <IconButton
            disableRipple
            onClick={handleClick}
            size="small"
            className={classes.button}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open}
          >
            {arrow}
          </IconButton>
        </TooltipOne>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        className={classes.sx}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        getContentAnchorEl={null}
      >
        <MenuItem disableRipple onClick={handleGetUserProfile}>
          <ListItemIcon style={{ margin: '0px' }}>
            <AccountCircleOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Mój profil
        </MenuItem>
        {adminExaminerPermission && (
          <MenuItem disableRipple onClick={handleSwitchMode}>
            <ListItemIcon>{iconModeType}</ListItemIcon>
            Przełącz na tryb {modeType}
          </MenuItem>
        )}
        {adminExaminerPermission && <Divider />}
        <MenuItem disableRipple onClick={handleLogoffMenuClick}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Wyloguj się
        </MenuItem>
      </Menu>
      <Prompt
        isBold={true}
        type="confirmation"
        open={openConfirmation}
        mainText={`Czy na pewno chcesz się wylogować z aplikacji ${t('global:shortNames.system')}?`}
        mainActionButtonText="TAK"
        onConfirm={handleLogoff}
        onCancel={handleLogoffMenuClick}
      />
      <UserProfile
        open={openUserProfile}
        user={profile}
        handleOpenUserProfile={handleGetUserProfile}
        onClose={handleUserProfileClose}
      />
    </>
  );
};

export default NavbarMenu;
